import {
  Box,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import FloridaAddressAutocomplete from "../FloridaAddressAutocomplete";
import { LoadingButton } from "@mui/lab";

import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";

import {
  setAutofillWorkType,
  loadPermitTemplate,
  setQuickActionAddress,
} from "../../../store/actions/dataActions";
import {
  refreshDiscountData,
  updateExtraCountyData,
  updateSelectedCounties,
} from "../../../store/actions/userActions";
import { refreshSubDiscountData } from "../../../store/actions/subUserActions";
import {
  refreshPPDiscountData,
  updatePPExtraCountyData,
} from "../../../store/actions/privateProviderActions";
import PurchaseExtraCounty from "../../popover/promoPopover/PurchaseExtraCounty";
import AddDefaultCardModal from "../../popover/promoPopover/AddDefaultCardModal";
import AddPaymentCardModal from "../../modals/AddPaymentCardModal";
import { mergeSelectedCountiesById } from "../../../utils/stateUtils";

const DEFAULT_GENERAL = "Alterations & Repairs";
const DEFAULT_INTEREST = "Fee Simple";

const QuickActionPermit = () => {
  const currentSession = useSelector((state) => state.sessions?.currentSession);
  const currentUser = useSelector((state) => state[currentSession]?.user);
  const userDetails = currentUser?.user;

  const [address, setAddress] = useState({});
  const [validateAddressLoading, setValidateAddressLoading] = useState(false);
  const [invalidAddressText, setInvalidAddressText] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [missingCounty, setMissingCounty] = useState(null);
  const [quickAddLoading, setQuickAddLoading] = useState(false);

  const [purchaseModalOpen, setPurchaseModalOpen] = useState(false);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [extraCountyTitle, setExtraCountyTitle] = useState("");
  const [countyValue, setCountyValue] = useState(1);
  const [paymentCards, setPaymentCards] = useState([]);
  const [defaultCard, setDefaultCard] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const [purchaseLoading, setPurchaseLoading] = useState(false);

  const stripe = useStripe();
  const elements = useElements();

  // Clear error message when a new valid address is selected
  useEffect(() => {
    if (address?.address?.full_address) {
      setInvalidAddressText("");
      setMissingCounty(null);
    }
  }, [address]);

  useEffect(() => {
    const transformedCards =
      currentUser?.payment_cards?.map((card) => {
        const lastTwo = String(card.exp_year).slice(-2);
        const first = card.exp_month;

        return {
          id: card.id,
          cardNum: "**** **** **** " + card.last4,
          cardName: card.name ? card.name : card.card_holder,
          expires:
            (first < 10 ? "0" + String(first) : String(first)) + "/" + lastTwo,
          default: card.default,
        };
      }) || [];

    setPaymentCards(transformedCards);
    const foundDefault = transformedCards.find((card) => card.default);
    setDefaultCard(foundDefault || null);
  }, [currentUser?.payment_cards]);

  const getCountyAllowance = () => {
    const planAllowance = parseInt(currentUser?.tier?.county || 0, 10);
    const analyticsAllowance = parseInt(
      currentUser?.user?.analytics?.additions?.numCounties || 0,
      10
    );
    const purchasedExtras = parseInt(
      currentUser?.tier?.additions?.county?.qty || 0,
      10
    );
    return planAllowance + analyticsAllowance + purchasedExtras;
  };

  const getAddedName = () => {
    if (currentUser?.subuser) {
      const first = currentUser?.subuser?.first || "";
      const last = currentUser?.subuser?.last || "";
      return first && last
        ? `${first?.charAt(0)?.toUpperCase()}.${last}`
        : currentUser?.subuser?.email || "";
    }

    if (userDetails?.role === "privateProvider") {
      const first = userDetails?.personal?.firstName || "";
      const last = userDetails?.personal?.lastName || "";
      return first && last
        ? `${first?.charAt(0)?.toUpperCase()}.${last}`
        : userDetails?.email || "";
    }

    const personal = userDetails?.personal?.[0];
    const first = personal?.firstName || "";
    const last = personal?.lastName || "";
    return first && last
      ? `${first?.charAt(0)?.toUpperCase()}.${last}`
      : userDetails?.email || "";
  };

  const buildCountyPayload = (countyName) => {
    const countyCard = currentUser?.displays?.county_cards?.jur_us_fl?.find(
      (card) => card.name === countyName,
    );

    if (!countyCard) {
      return null;
    }

    const generalValue =
      countyCard?.general ||
      countyCard?.defaults?.workType?.label ||
      DEFAULT_GENERAL;
    const interestValue =
      countyCard?.interest ||
      countyCard?.defaults?.ownerInterest?.label ||
      DEFAULT_INTEREST;

    return {
      ...countyCard,
      added_email: currentUser?.subuser
        ? currentUser?.subuser?.email
        : userDetails?.email,
      added_name: getAddedName(),
      locked: false,
      general: generalValue,
      interest: interestValue,
      jurisdiction_id: countyCard?.id,
    };
  };

  const handlePurchaseExtraCounty = async () => {
    try {
      setPurchaseLoading(true);
      const resp = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/stripe/add-subscription-county`,
        {
          currentSession: currentSession,
          num_county: countyValue,
        },
        { withCredentials: true }
      );

      if (resp.data.success) {
        if (currentSession === "privateProviderSession") {
          await dispatch(updatePPExtraCountyData(resp?.data?.data));
        } else {
          await dispatch(updateExtraCountyData(resp?.data?.data));
        }

        if (resp?.data?.discount) {
          if (currentSession === "privateProviderSession") {
            await dispatch(refreshPPDiscountData(resp?.data?.discount));
          } else if (currentSession === "subUserSession") {
            await dispatch(refreshSubDiscountData(resp?.data?.discount));
          } else {
            await dispatch(refreshDiscountData(resp?.data?.discount));
          }
        }

        setPurchaseModalOpen(false);
        setCountyValue(0);
      }
    } catch (error) {
      console.error("Failed to purchase extra county", error);
    } finally {
      setPurchaseLoading(false);
    }
  };

  const handleAddCountyFromDashboard = async () => {
    if (!missingCounty?.name || quickAddLoading) {
      return;
    }

    if (currentSession === "subUserSession") {
      setInvalidAddressText(
        "Please contact your administrator to manage county selections."
      );
      return;
    }

    const totalAllowance = getCountyAllowance();
    const selectedCount = currentUser?.user?.selectedCountiesNOC?.length || 0;

    if (totalAllowance > 0 && selectedCount >= totalAllowance) {
      setCountyValue(1);
      setPurchaseModalOpen(true);
      return;
    }

    const newCountyEntry = buildCountyPayload(missingCounty.name);

    if (!newCountyEntry) {
      setInvalidAddressText(
        `We couldn't load the ${missingCounty.name} county template. Please try again later.`
      );
      return;
    }

    const updatedPayload = [
      ...(currentUser?.user?.selectedCountiesNOC || []),
      newCountyEntry,
    ];

    try {
      setQuickAddLoading(true);

      const unlockResp = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/check-unlocked-select-counties`,
        { sessionName: currentSession },
        { withCredentials: true }
      );

      if (
        unlockResp?.data?.success === false ||
        unlockResp?.data?.is_locked === true
      ) {
        setInvalidAddressText(
          unlockResp?.data?.message ||
            "Counties are locked at the moment. Please try again shortly."
        );
        return;
      }

      const resp = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/selectnoc`,
        { selectedForm: updatedPayload, sessionName: currentSession },
        { withCredentials: true }
      );

      if (resp?.data?.success === false && resp?.data?.is_locked === true) {
        setInvalidAddressText(
          resp?.data?.message || "Unable to update counties right now."
        );
        return;
      }

       
      dispatch(
        updateSelectedCounties(
          mergeSelectedCountiesById(
            updatedPayload,
            currentUser?.displays?.county_cards,
          ),
        ),
      );
      setMissingCounty(null);
      setInvalidAddressText("");
      await handleValidateAddress({ countyOverride: updatedPayload });
    } catch (error) {
      setInvalidAddressText(
        error?.response?.data?.message ||
          "Unable to add the county right now. Please try again."
      );
      console.error("Quick county add failed:", error);
    } finally {
      setQuickAddLoading(false);
    }
  };

  const handleValidateAddress = async (options = {}) => {
    try {
      const invalidText =
        "Please enter a valid address with a house number and street name.";
      if (address?.address?.full_address?.trim()) {
        setValidateAddressLoading(true);
        // const trimmed = address?.address?.full_address?.trim();

        // if (!isValidAddress(trimmed) || Object.keys(address).length === 0) {
        if (Object.keys(address).length === 0) {
          setInvalidAddressText(invalidText);
          return;
        }
        // const result = await dispatch(getCountyAndCity(address));

        const countySource =
          options?.countyOverride ||
          currentUser?.user?.selectedCountiesNOC ||
          [];
        const countyNames = countySource.map((item) => item.name);

        // Convert slug to match selected county format (e.g., "st_johns" -> "St Johns")
        let county = address?.county?.slug
          ?.replace(/_/g, " ")
          .split(" ")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ");

        if (county === "Miami Dade") {
          county = "Miami-Dade";
        }
        if (!countyNames.includes(county)) {
          setInvalidAddressText(
            `The county "${county}" is not in your selected list. Please select it to proceed.`
          );
          setMissingCounty({ name: county });
          return;
        }

        setMissingCounty(null);
        await dispatch(loadPermitTemplate({ name: county }));
        await dispatch(
          setQuickActionAddress({
            fullStreetAddress: address?.address?.full_address,
            houseNumber: address?.address?.number,
            streetName: address?.address?.street,
            county: county,
            city: address?.city, // Already properly formatted from backend
            parcelID: address?.parcel_id,
            coNum: address?.county?.co_num,
          })
        );
        await dispatch(setAutofillWorkType(true));

        navigate("/permits/create");
      } else {
        setInvalidAddressText(invalidText);
      }
    } catch (e) {
      setInvalidAddressText(
        e?.message || "An error occurred. Please try again."
      );
      console.error("QuickActionPermit validation error:", e);
    } finally {
      setValidateAddressLoading(false);
      setTimeout(() => {
        setInvalidAddressText("");
      }, 5000);
    }
  };

  const isValidAddress = (input) => {
    const trimmed = input.trim();
    return /\d+/.test(trimmed) && /[a-zA-Z]{3,}/.test(trimmed);
  };

  const dispatch = useDispatch();
  const navigate = useNavigate();

  return (
    <>
      <Card
        sx={{
          boxShadow: "2px 6px 12px rgba(0, 0, 0, 0.5) !important",
        }}
      >
        <CardContent
          sx={{
            pt: 0,
            minHeight: "15rem",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
          }}
        >
          <Box>
            <Typography
              variant="h6"
              textAlign="center"
              gutterBottom
              mb={2}
              mt={2}
            >
              Create Permit
            </Typography>
            <FloridaAddressAutocomplete
              label="Property Address"
              onSelect={(val) => setAddress(val)}
              address={address}
              inputValue={inputValue}
              setInputValue={setInputValue}
              currentSession={currentSession}
            />
            <Box sx={{ my: 1, minHeight: "2rem" }}>
              <Typography color="error" variant="body2" textAlign="center">
                {invalidAddressText}
              </Typography>
              {missingCounty?.name && (
                <Box mt={1} display="flex" justifyContent="center">
                  <LoadingButton
                    loading={quickAddLoading}
                    variant="outlined"
                    color="primary"
                    onClick={handleAddCountyFromDashboard}
                  >
                    Add {missingCounty.name}
                  </LoadingButton>
                </Box>
              )}
            </Box>
          </Box>

          <Box textAlign="center" gap={1} sx={{ display: "flex" }}>
            <LoadingButton
              loading={validateAddressLoading}
              disabled={!address?.address?.full_address || !!invalidAddressText}
              fullWidth
              variant="contained"
              color="primary"
              onClick={() => handleValidateAddress()}
              endIcon={<AddIcon />}
            >
              Create
            </LoadingButton>
            <Tooltip title="Clear Address">
              <span>
                <IconButton
                  onClick={() => {
                    setAddress({});
                    setInputValue("");
                    setInvalidAddressText("");
                  }}
                  disabled={
                    !address?.address?.full_address || validateAddressLoading
                  }
                  sx={{
                    color: "error.main",
                    "&:hover": {
                      backgroundColor: "rgba(255, 0, 0, 0.1)",
                    },
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </CardContent>
      </Card>
      <PurchaseExtraCounty
        setPaymentModalOpen={setPaymentModalOpen}
        open={purchaseModalOpen}
        setOpen={setPurchaseModalOpen}
        setExtraCountyTitle={setExtraCountyTitle}
        defaultCard={defaultCard}
        handleAddCounty={handlePurchaseExtraCounty}
        addCountyLoading={purchaseLoading}
        countyValue={countyValue}
        setCountyValue={setCountyValue}
      />
      <AddDefaultCardModal
        open={paymentModalOpen}
        setOpen={setPaymentModalOpen}
        paymentCards={paymentCards}
        activeStep={activeStep}
        setActiveStep={setActiveStep}
        title={extraCountyTitle}
        defaultCard={defaultCard}
        checkDefault={true}
      />
      <AddPaymentCardModal
        stripe={stripe}
        elements={elements}
        CardElement={CardElement}
      />
    </>
  );
};

export default QuickActionPermit;
